/**
 * API Cache Utility
 * 
 * Simple in-memory cache for API responses that:
 * - Reduces redundant API calls
 * - Improves performance
 * - Provides automatic cache expiration (5 minutes)
 * - Supports cache clearing and removal
 * 
 * @module utils/apiCache
 */

const cache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const apiCache = {
  get(key) {
    const cached = cache.get(key);
    if (!cached) return null;
    
    const { data, timestamp } = cached;
    const age = Date.now() - timestamp;
    
    if (age > CACHE_DURATION) {
      cache.delete(key);
      return null;
    }
    
    return data;
  },
  
  set(key, data) {
    cache.set(key, {
      data,
      timestamp: Date.now()
    });
  },
  
  clear() {
    cache.clear();
  },
  
  remove(key) {
    cache.delete(key);
  }
};

/**
 * Wrapper for API calls with caching
 * @param {string} key - Cache key
 * @param {function} apiCall - Function that returns a promise
 * @returns {Promise} - Cached or fresh data
 */
export const cachedApiCall = async (key, apiCall) => {
  const cached = apiCache.get(key);
  if (cached) {
    return cached;
  }
  
  const result = await apiCall();
  apiCache.set(key, result);
  return result;
};
